import update from 'immutability-helper';
import isEqual from 'lodash/isEqual';
import { getRulesFromFilter } from '@utils';
import { createFilterRule } from '@utils/filter';
import * as types from './types';

const { baseUrl } = window.conviva;

const getFilterBarsObj = filterBars => filterBars.reduce((o, s) => ({ ...o, ...s.filters }), {});

const getDimensionDataObj = dataArr => dataArr.reduce((o, s) => ({ ...o, [[s.name].join('')]: 1 }), {});

const updateDimensionSelection = (dimensionSelection, dimensionName, filtersObj, dataObj) => {
  const obj = { ...dimensionSelection };
  const keys = Object.keys(filtersObj);

  /*
   *  selectionMode can be 0,1,2
   *  0 -> UNCHECKED
   *  1 -> CHECKED
   *  2 -> INDETERMINATE
   *  3 -> Disabled
   */
  let selectionMode;
  const uniqueKeys = new Set();
  keys.forEach((key) => {
    // key may have delimiter like __1 so two identical rules can be part of a filter
    const name = key.split('__');
    if (name[0] in dataObj) {
      uniqueKeys.add(name[0]);
    }
  });
  const inCount = uniqueKeys.size;
  if (inCount === 0) {
    selectionMode = 0;
  } else if (inCount > 0 && inCount < Object.keys(dataObj).length) {
    selectionMode = 2;
  } else {
    selectionMode = 1;
  }

  // dimensionSelection[dimensionName]
  obj[dimensionName] = selectionMode;
  return obj;
};

const updateAllDimensionSelection = (state, dimensionsObj) => {
  const { filters: { filterBars } } = state;
  let dimensionSelectionMode = {};

  Object.keys(dimensionsObj).forEach((dimensionName) => {
    dimensionSelectionMode = updateDimensionSelection(
      dimensionSelectionMode,
      dimensionName,
      getFilterBarsObj(filterBars),
      getDimensionDataObj(dimensionsObj[dimensionName]),
    );
  });

  return dimensionSelectionMode;
};

const initialState = {
  // mode is set by saga after checking persistence
  visible: false,
  nocUrl: {
    url: `${baseUrl}/dashboard/real-time`,
    type: '',
  },
  trends: {
    currentFilterBar: 0,
    filterBars: [
      {
        filters: {},
      },
    ],
    dimensionSelectionMode: {},
  },
  filters: {
    currentFilterBar: 0,
    filterBars: [
      {
        filters: {},
      },
    ],
    dimensionSelectionMode: {},
  },
  selectedDimensionInCompareMode: '',
};

const switchMap = {};

switchMap[types.INIT] = state => update(state, {
  $set: initialState,
});

switchMap[types.OPEN_NOC_DRAWER] = state => (update(state, {
  visible: { $set: true },
}));

switchMap[types.CLOSE_NOC_DRAWER] = state => (update(state, {
  visible: { $set: false },
}));

switchMap[types.SET_NOC_URL] = (state, action) => (update(state, {
  nocUrl: { $set: action.params },
}));

switchMap[types.RESET_TRENDS_FILTER] = state => (update(state, {
  trends: {
    currentFilterBar: { $set: 0 },
    filterBars: [
      {
        filters: { $set: {} },
      },
    ],
    dimensionSelectionMode: { $set: {} },
  },
}));

switchMap[types.RESET_FILTERS_FILTER] = state => (update(state, {
  filters: {
    currentFilterBar: { $set: 0 },
    filterBars: [
      {
        filters: { $set: {} },
      },
    ],
    dimensionSelectionMode: { $set: {} },
  },
}));

switchMap[types.SET_INITIAL_TRENDS_FILTER_TO_DRAWER] = (state, action) => {
  const { appliedFilter } = action.params;
  const tempFilters = [];
  // eslint-disable-next-line array-callback-return
  appliedFilter.map((filter) => {
    const rules = getRulesFromFilter(filter.rule, false);
    tempFilters.push({
      filters: rules,
      ...(filter.fid ? { fid: filter.fid, name: filter.name } : {}),
    });
  });
  return (update(state, {
    trends: {
      currentFilterBar: { $set: 0 },
      filterBars: { $set: tempFilters },
      dimensionSelectionMode: { $set: {} },
    },
  })
  );
};

switchMap[types.SET_INITIAL_FILTERS_TO_DRAWER] = (state, action) => {
  const { appliedFilter } = action.params;
  const tempFilters = [];
  // eslint-disable-next-line array-callback-return
  appliedFilter.map((filter) => {
    const rules = getRulesFromFilter(filter.rule, false);
    tempFilters.push({
      filters: rules,
      ...(filter.fid ? { fid: filter.fid, name: filter.name } : {}),
    });
  });
  return (update(state, {
    filters: {
      currentFilterBar: { $set: 0 },
      filterBars: { $set: tempFilters },
      dimensionSelectionMode: { $set: {} },
    },
  })
  );
};

switchMap[types.TOGGLE_DIMENSION_IN_TRENDS_NOC_DRAWER] = (state, action) => {
  const clonedState = JSON.parse(JSON.stringify(state));
  const filterBar = clonedState.trends.filterBars[clonedState.trends.currentFilterBar];
  if (filterBar.filters.length === 0) {
    // add dimension to the new rule
    filterBar.filters = {
      [action.filterRule.display]: action.filterRule,
    };
  } else if (action.filterRule.display in filterBar.filters) {
    // delete dimension from the rule
    delete filterBar.filters[action.filterRule.display];
  } else {
    // add dimension to the rule
    filterBar.filters[action.filterRule.display] = action.filterRule;
  }
  clonedState.trends.dimensionSelectionMode = updateDimensionSelection(
    clonedState.trends.dimensionSelectionMode,
    action.dimensionName,
    getFilterBarsObj(clonedState.trends.filterBars),
    action.dimensionDataObj,
  );
  return clonedState;
};

switchMap[types.TOGGLE_DIMENSION_IN_FILTERS_NOC_DRAWER] = (state, action) => {
  const clonedState = JSON.parse(JSON.stringify(state));
  const filterBar = clonedState.filters.filterBars[clonedState.filters.currentFilterBar];
  if (filterBar.filters.length === 0) {
    // add dimension to the new rule
    filterBar.filters = {
      [action.filterRule.display]: action.filterRule,
    };
  } else if (action.filterRule.display in filterBar.filters) {
    // delete dimension from the rule
    delete filterBar.filters[action.filterRule.display];
  } else {
    // add dimension to the rule
    filterBar.filters[action.filterRule.display] = action.filterRule;
  }
  clonedState.filters.dimensionSelectionMode = updateDimensionSelection(
    clonedState.filters.dimensionSelectionMode,
    action.dimensionName,
    getFilterBarsObj(clonedState.filters.filterBars),
    action.dimensionDataObj,
  );
  return clonedState;
};

switchMap[types.SELECT_NOC_TRENDS_FILTER_BAR] = (state, action) => update(state, {
  trends: {
    currentFilterBar: {
      $set: action.currentFilterBar,
    },
  },
});

switchMap[types.SELECT_NOC_FILTERS_FILTER_BAR] = (state, action) => update(state, {
  filters: {
    currentFilterBar: {
      $set: action.currentFilterBar,
    },
  },
});

switchMap[types.ADD_NEW_NOC_TRENDS_FILTER_BAR] = (state, action) => {
  const clonedState = JSON.parse(JSON.stringify(state));
  const newFilterBar = initialState.trends.filterBars[0];
  newFilterBar.filters = action.scope;
  clonedState.trends.filterBars.push(newFilterBar);
  clonedState.trends.currentFilterBar = clonedState.trends.filterBars.length - 1;
  return clonedState;
};

switchMap[types.ADD_NEW_NOC_FILTERS_FILTER_BAR] = (state, action) => {
  const clonedState = JSON.parse(JSON.stringify(state));
  const newFilterBar = initialState.filters.filterBars[0];
  newFilterBar.filters = action.scope;
  clonedState.filters.filterBars.push(newFilterBar);
  clonedState.filters.currentFilterBar = clonedState.filters.filterBars.length - 1;
  return clonedState;
};

switchMap[types.DELETE_NOC_TRENDS_FILTER_BAR] = (state, action) => update(state, {
  trends: {
    currentFilterBar: {
      $apply: currentVal => currentVal + action.newFilterBarDelta,
    },
    filterBars: {
      $splice: [[action.index, 1]],
    },
    dimensionSelectionMode: {
      $apply: () => updateAllDimensionSelection(state, action.dimensionsObj),
    },
  },
});

switchMap[types.DELETE_NOC_FILTERS_FILTER_BAR] = (state, action) => update(state, {
  filters: {
    currentFilterBar: {
      $apply: currentVal => currentVal + action.newFilterBarDelta,
    },
    filterBars: {
      $splice: [[action.index, 1]],
    },
    dimensionSelectionMode: {
      $apply: () => updateAllDimensionSelection(state, action.dimensionsObj),
    },
  },
});

switchMap[types.SELECT_MULTIPLE_DIMENSION_FROM_DIMESION_HEADER] = (state, action) => {
  const {
    dimensionName,
    appliedRules,
    dimensionList,
    dimensionData,
    savedFiltersCount,
    MAX_NOC_FILTERS,
    SOFT_MAX_NOC_FILTERS,
  } = action;
  const { filters: { filterBars } } = state;
  let currentFilterBars = update(filterBars, {});
  currentFilterBars = currentFilterBars.filter(
    filterBar => !isEqual(filterBar.filters, appliedRules),
  );
  // find the initial _order for the rule
  let nextOrder = -1;
  Object.values(appliedRules).forEach((rule) => {
    if (rule._order > nextOrder) { // eslint-disable-line
      nextOrder = rule._order;  // eslint-disable-line
    }
  });
  const newFilterBars = [...currentFilterBars];
  let i = 0;
  let dimension = dimensionData[i];
  for (i = 1;
    (i <= SOFT_MAX_NOC_FILTERS
      && (newFilterBars.length + savedFiltersCount) < MAX_NOC_FILTERS
      && dimension);
    i += 1) {
    const name = [dimension.name].join('');
    const value = [dimension.value].join('');
    const rule = {
      ...createFilterRule(dimensionList, dimensionName, name, value),
      _order: nextOrder + 1,
    };
    const filters = {
      ...appliedRules,
      [`${rule.display}__${i}`]: rule,
    };
    newFilterBars.push({ filters });
    dimension = dimensionData[i];
  }
  const dimensionSelectionMode = update(state.filters.dimensionSelectionMode, {});
  return update(state, {
    filters: {
      filterBars: { $set: newFilterBars },
      currentFilterBar: { $set: newFilterBars.length - 1 },
      dimensionSelectionMode: {
        $set: updateDimensionSelection(
          dimensionSelectionMode,
          action.dimensionName,
          getFilterBarsObj(newFilterBars),
          getDimensionDataObj(action.dimensionData),
        ),
      },
    },
  });
};

switchMap[types.UNSELECT_MULTIPLE_DIMENSION_FROM_DIMESION_HEADER] = (state, action) => {
  const {
    appliedRules,
    dimensionData,
  } = action;
  const { filters: { filterBars } } = state;
  const tempFilterBars = update(filterBars, {});
  const dataObj = dimensionData.reduce((agg, item) => ({ ...agg, [[item.name].join('')]: 1 }), {});
  for (let j = tempFilterBars.length - 1; j >= 0; j -= 1) {
    const ruleNamesList = Object.keys(tempFilterBars[j].filters);
    for (let i = 0; i < ruleNamesList.length; i += 1) {
      if (ruleNamesList[i].split('__')[0] in dataObj) {
        tempFilterBars.splice(j, 1);
        break;
      }
    }
  }
  let newFilterBars = [];
  if (tempFilterBars.length === 0) {
    const scope = [
      {
        filters: {
          ...appliedRules,
        },
      },
    ];
    newFilterBars = [...scope];
  } else {
    newFilterBars = [...tempFilterBars];
  }
  const dimensionSelectionMode = update(state.filters.dimensionSelectionMode, {});
  return update(state, {
    filters: {
      filterBars: { $set: newFilterBars },
      currentFilterBar: { $set: newFilterBars.length - 1 },
      dimensionSelectionMode: {
        $set: updateDimensionSelection(
          dimensionSelectionMode,
          action.dimensionName,
          getFilterBarsObj(newFilterBars),
          dataObj,
        ),
      },
    },
  });
};

// eslint-disable-next-line max-len
export default (state = initialState, action) => (switchMap[action.type] ? switchMap[action.type](state, action) : state);
